/**
 * 1.9.0 overnight (D-1102-OFFICE-1) — "Might slip — check these": a short list on Today for the administrator and a
 * co-administrator of the things that are not wrong yet but are close to it. `GET /api/might-slip`.
 *
 * Five kinds, each from data the app already keeps, each with one action that opens the screen where it is fixed:
 *
 *   unconfirmedVisit   a Housekeeping visit inside the next 48 hours that the tenant has not confirmed
 *   noVolunteer        a Companion & Wellness request inside the next 7 days with no confirmed, Ready volunteer
 *   eventBelowTarget   an event inside the next 7 days whose tenant RSVPs are below the event's own places (only
 *                      where it has a number — nothing is invented)
 *   checkIn            a tenant whose last two Housekeeping visits ended No Show or Unable to Complete
 *   agreementWaiting   an agreement link opened and not signed after 24 hours, or never opened after 3 days
 *
 * It reads and shows. Nothing here sends, books or changes anything. Counts and names only, and only for the roles
 * that already see those people: the scheduler, the viewer, the crew and the tech get no list at all.
 *
 * No dependencies, so the app and the API share it.
 */
import type { TenantAgreement } from './agreement-cards';
import type { Role } from './roles';

export const MIGHT_SLIP_KINDS = ['unconfirmedVisit', 'noVolunteer', 'eventBelowTarget', 'checkIn', 'agreementWaiting'] as const;
export type MightSlipKind = (typeof MIGHT_SLIP_KINDS)[number];

/** The Admin → Settings switch. On unless the row says otherwise. */
export const MIGHT_SLIP_SETTING = 'MIGHT_SLIP_ENABLED';

/** The roles that get the list (the office). Everyone else is told nothing, not even that it exists. */
export const MIGHT_SLIP_ROLES: readonly Role[] = ['admin', 'coadmin'];
export const mightSlipRole = (role: Role | null | undefined): boolean => !!role && MIGHT_SLIP_ROLES.includes(role);

/** The windows, said once. */
export const MIGHT_SLIP_RULES = {
  /** Housekeeping visits not confirmed: from now to this many hours ahead. */
  visitHours: 48,
  /** Companion & Wellness requests with no Ready volunteer: this many days ahead (today included). */
  requestDays: 7,
  /** Events below their places: this many days ahead (today included). */
  eventDays: 7,
  /** Last two visits ended No Show / Unable to Complete: the newest of the two must be this recent. */
  checkInRecentDays: 60,
  /** …and how far back the visits are read. */
  checkInLookBackDays: 90,
  /** An agreement link opened and not signed for this many hours. */
  openedHours: 24,
  /** An agreement link sent and never opened for this many days. */
  neverOpenedDays: 3,
} as const;

/** The program a kind belongs to, which decides who may see it (src/app/nav.ts ROUTE_ROLES carries the same names). */
export const MIGHT_SLIP_ROUTE: Record<MightSlipKind, string> = {
  unconfirmedVisit: '/housekeeping',
  noVolunteer: '/companion',
  eventBelowTarget: '/events',
  checkIn: '/people/:tenantId',
  agreementWaiting: '/people/:tenantId',
};

export interface MightSlipLine {
  /** Unique and stable: `<kind>:<what it is about>`. */
  id: string;
  kind: MightSlipKind;
  /** The day it is about (a visit's day, a request's next visit, an event's day, the last visit's day); null when it is not about a day. */
  dayKey: string | null;
  /** A tenant "1208 · Name" — office roles only; null on a line that is not about one tenant. */
  who: string | null;
  /** An event's name. */
  label: string | null;
  /** Places taken / places, for an event. */
  have: number | null;
  need: number | null;
  /** The activity word for a Companion request: `walk`, `trip`, `other`. */
  activity: string | null;
  /** An agreement line: which one, how it stands, and for how many days. */
  agreement: TenantAgreement | null;
  standing: 'opened' | 'neverOpened' | null;
  days: number | null;
  /** The address of the one action. */
  href: string;
}

export interface MightSlipResponse {
  /** The Settings switch (false: the list is off, `lines` is empty). */
  enabled: boolean;
  todayKey: string;
  lines: MightSlipLine[];
  /** Kinds that could not be read just now (a list or a file the site does not have yet) — the rest is still drawn. */
  unavailable?: MightSlipKind[];
}

/** An empty line with every field spelled out. */
export const slipLine = (kind: MightSlipKind, ref: string, href: string, o: Partial<MightSlipLine> = {}): MightSlipLine => ({
  id: `${kind}:${ref}`,
  kind,
  dayKey: null,
  who: null,
  label: null,
  have: null,
  need: null,
  activity: null,
  agreement: null,
  standing: null,
  days: null,
  href,
  ...o,
});

/** Kinds first, then the nearest day first, then by id — the order the list is drawn in. */
export function sortSlip(lines: readonly MightSlipLine[]): MightSlipLine[] {
  const order = (k: MightSlipKind) => MIGHT_SLIP_KINDS.indexOf(k);
  return [...lines].sort((a, b) => order(a.kind) - order(b.kind) || (a.dayKey ?? '99999999').localeCompare(b.dayKey ?? '99999999') || a.id.localeCompare(b.id));
}

/**
 * The tenant's last two Housekeeping visits both ended without the visit: the two newest (by day) of the visits that
 * ended — Completed, No Show or Unable to Complete — are both No Show / Unable to Complete. Pure; `visits` are one
 * tenant's, any order. Returns the newest of the two, or null.
 */
export function lastTwoMissed(visits: readonly { dayKey: string; status: string }[]): { dayKey: string; status: string } | null {
  const ended = visits.filter((v) => v.status === 'Completed' || v.status === 'No Show' || v.status === 'Unable to Complete').sort((a, b) => b.dayKey.localeCompare(a.dayKey));
  const [a, b] = ended;
  if (!a || !b) return null;
  const missed = (s: string) => s === 'No Show' || s === 'Unable to Complete';
  return missed(a.status) && missed(b.status) ? a : null;
}
